import { ColorRank, GameCard } from "../types/battle"
import { DominantLeague } from "../types/faction"
import {
  AirshipActiveMission,
  AirshipCargoFullness,
  AirshipCargoGroup,
  AirshipCargoItem,
  AirshipCityOption,
  AirshipClaimResponse,
  AirshipCombatCard,
  AirshipCombatLog,
  AirshipDockStatus,
  AirshipGeoBody,
  AirshipGuardTier,
  AirshipGuards,
  AirshipHangarPoint,
  AirshipInfo,
  AirshipInventoryMeta,
  AirshipLatLng,
  AirshipMapMission,
  AirshipMissionFlightStatus,
  AirshipMissionRoute,
  AirshipRaidConfig,
  AirshipRaidHistoryItem,
  AirshipRaidOutcome,
  AirshipRaidResult,
  AirshipStatus,
  AirshipUpgradeInfo,
} from "../types/airship"
import { normalizeColorRank } from "./radarStatus"
import { normalizeGameCard, normalizeGameCardsList } from "./gameCards"

type RawRecord = Record<string, unknown>

export const AIRSHIP_PURCHASE_COINS_DEFAULT = 15_000
export const AIRSHIP_TRIP_FEE_COINS_DEFAULT = 100
export const AIRSHIP_GUARD_SLOTS = 3

export const AIRSHIP_LEVEL_TABLE: Array<{
  level: number
  scoutPoints: number
  upgradePriceCoins: number | null
}> = [
  { level: 1, scoutPoints: 1, upgradePriceCoins: null },
  { level: 2, scoutPoints: 3, upgradePriceCoins: 4_000 },
  { level: 3, scoutPoints: 5, upgradePriceCoins: 8_000 },
  { level: 4, scoutPoints: 8, upgradePriceCoins: 16_000 },
  { level: 5, scoutPoints: 16, upgradePriceCoins: 50_000 },
]

export const DEFAULT_AIRSHIP_RAID_CONFIG: AirshipRaidConfig = {
  scoutEnergyCost: 5,
  attackEnergyCost: 30,
  attackCoinsCost: 300,
  attackDailyLimit: 3,
  attackCardMax: 3,
  winPts: 20,
  losePts: 5,
  autoBattle: true,
}

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") {
    const trimmed = value.trim()
    return trimmed || fallback
  }
  if (value != null) {
    const asString = String(value).trim()
    return asString || fallback
  }
  return fallback
}

function pickNumber(value: unknown, fallback: number | null = null): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value.trim().replace(",", "."))
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

function pickBool(value: unknown, fallback = false): boolean {
  if (typeof value === "boolean") return value
  if (value === 1 || value === "1") return true
  if (value === 0 || value === "0") return false
  return fallback
}

function unwrapPayload(data: unknown): RawRecord {
  if (!data || typeof data !== "object" || Array.isArray(data)) return {}
  const obj = data as RawRecord
  const nested = obj.data ?? obj.result ?? obj.payload
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return { ...(nested as RawRecord), ...obj }
  }
  return obj
}

function normalizeDockStatus(raw: unknown): AirshipDockStatus | null {
  const key = pickString(raw).toUpperCase()
  if (key === "DOCKED" || key === "IN_MISSION") return key
  if (key === "DOCK" || key === "IDLE") return "DOCKED"
  if (key === "MISSION" || key === "FLYING" || key === "IN_FLIGHT") return "IN_MISSION"
  return null
}

function normalizeFlightStatus(raw: unknown): AirshipMissionFlightStatus | null {
  const key = pickString(raw).toUpperCase()
  if (key === "IN_FLIGHT" || key === "OUTBOUND" || key === "TO_CITY") return "IN_FLIGHT"
  if (key === "RETURNING" || key === "RETURN" || key === "HOMEBOUND") return "RETURNING"
  return null
}

function normalizeGuardTier(raw: unknown): AirshipGuardTier | null {
  if (typeof raw === "number" && Number.isFinite(raw)) {
    if (raw >= 3) return "STRONG"
    if (raw >= 2) return "MEDIUM"
    if (raw >= 1) return "WEAK"
    return null
  }
  const key = pickString(raw).toUpperCase().replace(/\s+/g, "_")
  if (!key || key === "NONE" || key === "NULL" || key === "EMPTY" || key === "NO") {
    return null
  }
  if (key === "WEAK" || key === "LOW" || key === "СЛАБАЯ" || key === "1") return "WEAK"
  if (
    key === "MEDIUM" ||
    key === "MED" ||
    key === "MID" ||
    key === "СРЕДНЯЯ" ||
    key === "2"
  ) {
    return "MEDIUM"
  }
  if (key === "STRONG" || key === "HIGH" || key === "СИЛЬНАЯ" || key === "3") {
    return "STRONG"
  }
  return null
}

function normalizeGuardCardIds(raw: unknown): string[] {
  if (raw == null) return []
  if (typeof raw === "string") {
    const trimmed = raw.trim()
    if (!trimmed) return []
    if (trimmed.startsWith("[")) {
      try {
        return normalizeGuardCardIds(JSON.parse(trimmed))
      } catch {
        // fall through to split
      }
    }
    return trimmed
      .split(/[,;\s]+/)
      .map((id) => id.trim())
      .filter(Boolean)
  }
  if (!Array.isArray(raw)) return []
  const ids: string[] = []
  for (const item of raw) {
    if (item == null) continue
    if (typeof item === "string" || typeof item === "number") {
      const id = String(item).trim()
      if (id) ids.push(id)
      continue
    }
    if (typeof item === "object") {
      const rec = item as RawRecord
      const id = rec.id ?? rec.uuid ?? rec.cardId ?? rec.card_id
      if (id != null && String(id).trim()) ids.push(String(id).trim())
    }
  }
  return ids
}

function normalizeCargoFullness(raw: unknown): AirshipCargoFullness | null {
  const key = pickString(raw).toUpperCase()
  if (key === "EMPTY" || key === "LOW" || key === "HIGH") return key
  return null
}

function normalizeLatLng(raw: unknown): AirshipLatLng | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  const lat = pickNumber(item.lat ?? item.latitude)
  const lng = pickNumber(item.lng ?? item.longitude ?? item.lon)
  if (lat == null || lng == null) return null
  return { lat, lng }
}

function normalizeRoute(raw: unknown): AirshipMissionRoute | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  const from = normalizeLatLng(item.from ?? item.hangar ?? item.origin)
  const to = normalizeLatLng(item.to ?? item.city ?? item.destination)
  if (!from || !to) return null
  return { from, to }
}

export function normalizeAirshipHangarPoint(raw: unknown): AirshipHangarPoint | null {
  if (raw == null) return null
  if (typeof raw === "string" && raw.trim()) {
    try {
      return normalizeAirshipHangarPoint(JSON.parse(raw))
    } catch {
      return null
    }
  }
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = unwrapPayload(raw)
  const nested =
    item.hangar ?? item.point ?? item.hangarPoint ?? item.hangar_point
  if (nested && nested !== item && typeof nested === "object") {
    const fromNested = normalizeAirshipHangarPoint(nested)
    if (fromNested) return fromNested
  }

  const id = item.id ?? item.uuid ?? item.pointId ?? item.point_id
  const lat = pickNumber(item.latitude ?? item.lat)
  const lng = pickNumber(item.longitude ?? item.lng ?? item.lon)
  if (id == null || lat == null || lng == null) return null

  return {
    id: String(id),
    name: pickString(item.name ?? item.title, "Ангар"),
    latitude: lat,
    longitude: lng,
    radiusMeters:
      pickNumber(
        item.radiusMeters ?? item.radius_meters ?? item.radiusM ?? item.radius,
        50
      ) ?? 50,
    cityId: pickNumber(item.cityId ?? item.city_id) ?? undefined,
  }
}

function normalizeUpgradeInfo(raw: unknown): AirshipUpgradeInfo | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  const level = pickNumber(item.level ?? item.toLevel ?? item.to_level)
  const points = pickNumber(
    item.points ??
      item.scannablePoints ??
      item.scannable_points ??
      item.scoutPoints ??
      item.scout_points
  )
  const coins = pickNumber(
    item.coins ?? item.priceCoins ?? item.price_coins ?? item.cost
  )
  if (level == null || points == null || coins == null) return null
  return { level, points, coins }
}

export function normalizeAirshipCargoItem(raw: unknown): AirshipCargoItem | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  const lootType = pickString(
    item.lootType ?? item.loot_type ?? item.type,
    "COINS"
  ).toUpperCase()
  const cardRaw = item.card
  const card =
    cardRaw && typeof cardRaw === "object"
      ? normalizeGameCard(cardRaw) ?? null
      : null
  const colorRankRaw = item.colorRank ?? item.color_rank
  const colorRank = colorRankRaw
    ? normalizeColorRank(String(colorRankRaw))
    : card?.colorRank ?? null

  return {
    id: item.id != null ? String(item.id) : undefined,
    lootType,
    amount: pickNumber(item.amount ?? item.value ?? item.coins) ?? undefined,
    colorRank: (colorRank as ColorRank | null) ?? null,
    card,
    pointName: pickString(item.pointName ?? item.point_name) || undefined,
    title: pickString(item.title ?? item.name) || undefined,
    missionId:
      item.missionId != null || item.mission_id != null
        ? String(item.missionId ?? item.mission_id)
        : undefined,
    cityTitle:
      pickString(item.cityTitle ?? item.city_title ?? item.toCityTitle) ||
      undefined,
    toCityId:
      pickNumber(item.toCityId ?? item.to_city_id ?? item.cityId) ?? undefined,
    departedAt: pickString(item.departedAt ?? item.departed_at) || null,
  }
}

function emptyGuards(): AirshipGuards {
  return {
    cardIds: [],
    cards: [],
    power: 0,
    tier: null,
    slots: AIRSHIP_GUARD_SLOTS,
  }
}

function normalizeGuards(raw: unknown): AirshipGuards {
  if (raw == null) return emptyGuards()

  // Backend иногда отдаёт просто список карт или id.
  if (Array.isArray(raw)) {
    const cards = normalizeGameCardsList(raw)
    const cardIds =
      cards.length > 0 ? cards.map((card) => card.id) : normalizeGuardCardIds(raw)
    const power = cards.reduce((sum, card) => sum + (card.basePower ?? 0), 0)
    return {
      cardIds,
      cards,
      power,
      tier: null,
      slots: AIRSHIP_GUARD_SLOTS,
    }
  }

  // tier строкой: guard: "MEDIUM"
  if (typeof raw === "string" || typeof raw === "number") {
    return {
      ...emptyGuards(),
      tier: normalizeGuardTier(raw),
    }
  }

  if (typeof raw !== "object") return emptyGuards()

  const item = raw as RawRecord
  const cards = normalizeGameCardsList(
    item.cards ??
      item.items ??
      item.guardCards ??
      item.guard_cards ??
      item.defenseCards ??
      item.defense_cards
  )
  const cardIdsFromFields = normalizeGuardCardIds(
    item.cardIds ??
      item.card_ids ??
      item.ids ??
      item.guardCardIds ??
      item.guard_card_ids
  )
  // Если cards — массив uuid, а не объектов.
  const cardIdsFromCardsField =
    cards.length === 0 ? normalizeGuardCardIds(item.cards) : []
  const cardIds =
    cardIdsFromFields.length > 0
      ? cardIdsFromFields
      : cards.length > 0
        ? cards.map((card) => card.id)
        : cardIdsFromCardsField

  const powerFromCards = cards.reduce(
    (sum, card) => sum + (card.basePower ?? 0),
    0
  )
  const power =
    pickNumber(
      item.power ??
        item.totalPower ??
        item.total_power ??
        item.guardPower ??
        item.guard_power ??
        item.defensePower ??
        item.defense_power,
      powerFromCards
    ) ?? powerFromCards

  return {
    cardIds,
    cards,
    power,
    tier: normalizeGuardTier(
      item.tier ?? item.guardTier ?? item.guard_tier ?? item.level
    ),
    slots:
      pickNumber(item.slots ?? item.maxSlots ?? item.max_slots, AIRSHIP_GUARD_SLOTS) ??
      AIRSHIP_GUARD_SLOTS,
  }
}

function mergeGuards(...parts: AirshipGuards[]): AirshipGuards {
  const tierRank = { WEAK: 1, MEDIUM: 2, STRONG: 3 } as const
  const base = emptyGuards()
  let bestTier: AirshipGuardTier | null = null
  for (const part of parts) {
    if (part.cardIds.length > 0 && base.cardIds.length === 0) {
      base.cardIds = part.cardIds
    }
    if (part.cards.length > 0 && base.cards.length === 0) {
      base.cards = part.cards
    }
    if (part.power > base.power) base.power = part.power
    if (part.slots > 0) base.slots = part.slots
    if (
      part.tier &&
      (!bestTier || tierRank[part.tier] > tierRank[bestTier])
    ) {
      bestTier = part.tier
    }
  }
  if (base.cardIds.length === 0 && base.cards.length > 0) {
    base.cardIds = base.cards.map((card) => card.id)
  }
  base.tier = bestTier
  return base
}

function normalizeAirshipInfo(raw: unknown): AirshipInfo | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  const scoutLevel =
    pickNumber(item.scoutLevel ?? item.scout_level ?? item.level, 1) ?? 1
  const table = AIRSHIP_LEVEL_TABLE.find((row) => row.level === scoutLevel)
  const status =
    normalizeDockStatus(item.status ?? item.state ?? item.dockStatus) ?? "DOCKED"
  const cargoRaw = item.cargo ?? item.loot ?? item.hold
  const cargo = Array.isArray(cargoRaw)
    ? cargoRaw
        .map(normalizeAirshipCargoItem)
        .filter((c): c is AirshipCargoItem => c != null)
    : []

  return {
    scoutLevel,
    scoutPoints:
      pickNumber(
        item.scoutPoints ??
          item.scout_points ??
          item.scannablePoints ??
          item.scannable_points ??
          item.points,
        table?.scoutPoints ?? 1
      ) ??
      table?.scoutPoints ??
      1,
    status,
    tripCount: pickNumber(item.tripCount ?? item.trip_count, 0) ?? 0,
    cargo,
    cargoCount:
      pickNumber(item.cargoCount ?? item.cargo_count, cargo.length) ?? cargo.length,
    guards: mergeGuards(
      normalizeGuards(item.guards ?? item.guard),
      normalizeGuards({
        cardIds:
          item.guardCardIds ??
          item.guard_card_ids ??
          item.cardIds ??
          item.card_ids,
        cards: item.guardCards ?? item.guard_cards,
        power:
          item.guardPower ??
          item.guard_power ??
          item.defensePower ??
          item.defense_power,
        tier: item.guardTier ?? item.guard_tier,
        slots: item.guardSlots ?? item.guard_slots,
      })
    ),
    nextUpgrade: normalizeUpgradeInfo(
      item.nextUpgrade ?? item.next_upgrade ?? item.upgrade
    ),
  }
}

function normalizeActiveMission(raw: unknown): AirshipActiveMission | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  const id = item.id ?? item.missionId ?? item.mission_id
  if (id == null) return null
  const status =
    normalizeFlightStatus(item.status ?? item.phase ?? item.leg) ?? "IN_FLIGHT"
  const progressRaw = pickNumber(item.progress, 0) ?? 0

  return {
    id: String(id),
    status,
    toCityId:
      pickNumber(item.toCityId ?? item.to_city_id ?? item.cityId ?? item.city_id) ??
      undefined,
    toCityTitle:
      pickString(
        item.toCityTitle ?? item.to_city_title ?? item.cityTitle ?? item.city_title
      ) || undefined,
    departedAt: pickString(item.departedAt ?? item.departed_at) || null,
    arriveAt: pickString(item.arriveAt ?? item.arrive_at ?? item.arrivalAt) || null,
    returnAt: pickString(item.returnAt ?? item.return_at) || null,
    progress: Math.max(0, Math.min(1, progressRaw)),
    currentPos: normalizeLatLng(item.currentPos ?? item.current_pos ?? item.position),
    route: normalizeRoute(item.route),
  }
}

function normalizeInventory(raw: unknown): AirshipInventoryMeta {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { slotsLeft: 0 }
  }
  const item = raw as RawRecord
  const slotsLeft =
    pickNumber(
      item.slotsLeft ?? item.slots_left ?? item.freeSlots ?? item.free_slots,
      0
    ) ?? 0
  const capacity =
    pickNumber(item.capacity ?? item.cardCapacity ?? item.card_capacity) ??
    undefined
  let used =
    pickNumber(item.used ?? item.cardsCount ?? item.cards_count) ?? undefined
  if (used == null && capacity != null) {
    used = Math.max(0, capacity - slotsLeft)
  }
  return { slotsLeft, capacity, used }
}

function normalizeRaidConfig(raw: unknown): AirshipRaidConfig {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { ...DEFAULT_AIRSHIP_RAID_CONFIG }
  }
  const item = raw as RawRecord
  return {
    scoutEnergyCost:
      pickNumber(
        item.scoutEnergyCost ?? item.scout_energy_cost,
        DEFAULT_AIRSHIP_RAID_CONFIG.scoutEnergyCost
      ) ?? DEFAULT_AIRSHIP_RAID_CONFIG.scoutEnergyCost,
    attackEnergyCost:
      pickNumber(
        item.attackEnergyCost ?? item.attack_energy_cost,
        DEFAULT_AIRSHIP_RAID_CONFIG.attackEnergyCost
      ) ?? DEFAULT_AIRSHIP_RAID_CONFIG.attackEnergyCost,
    attackCoinsCost:
      pickNumber(
        item.attackCoinsCost ?? item.attack_coins_cost,
        DEFAULT_AIRSHIP_RAID_CONFIG.attackCoinsCost
      ) ?? DEFAULT_AIRSHIP_RAID_CONFIG.attackCoinsCost,
    attackDailyLimit:
      pickNumber(
        item.attackDailyLimit ?? item.attack_daily_limit,
        DEFAULT_AIRSHIP_RAID_CONFIG.attackDailyLimit
      ) ?? DEFAULT_AIRSHIP_RAID_CONFIG.attackDailyLimit,
    attackCardMax:
      pickNumber(
        item.attackCardMax ?? item.attack_card_max,
        DEFAULT_AIRSHIP_RAID_CONFIG.attackCardMax
      ) ?? DEFAULT_AIRSHIP_RAID_CONFIG.attackCardMax,
    winPts:
      pickNumber(item.winPts ?? item.win_pts, DEFAULT_AIRSHIP_RAID_CONFIG.winPts) ??
      DEFAULT_AIRSHIP_RAID_CONFIG.winPts,
    losePts:
      pickNumber(item.losePts ?? item.lose_pts, DEFAULT_AIRSHIP_RAID_CONFIG.losePts) ??
      DEFAULT_AIRSHIP_RAID_CONFIG.losePts,
    autoBattle: pickBool(
      item.autoBattle ?? item.auto_battle,
      DEFAULT_AIRSHIP_RAID_CONFIG.autoBattle
    ),
  }
}

export function normalizeAirshipStatus(data: unknown): AirshipStatus | null {
  if (data == null) return null
  const obj = unwrapPayload(data)
  const hangar = normalizeAirshipHangarPoint(
    obj.hangar ?? obj.hangarPoint ?? obj.hangar_point ?? obj.point
  )
  const airshipRaw = obj.airship ?? obj.dirigible ?? obj.ship
  const airship =
    airshipRaw === false || airshipRaw === null
      ? null
      : normalizeAirshipInfo(airshipRaw)

  const hasAirshipFlag = pickBool(obj.hasAirship ?? obj.has_airship, airship != null)
  const topCargoRaw = obj.cargo ?? obj.loot ?? obj.hold
  const topCargo = Array.isArray(topCargoRaw)
    ? topCargoRaw
        .map(normalizeAirshipCargoItem)
        .filter((item): item is AirshipCargoItem => item != null)
    : airship?.cargo ?? []
  const inventory = normalizeInventory(
    obj.inventory ?? obj.backpack ?? obj.cardInventory ?? obj.card_inventory
  )
  if (
    inventory.slotsLeft === 0 &&
    (obj.slotsLeft != null || obj.slots_left != null)
  ) {
    inventory.slotsLeft =
      pickNumber(obj.slotsLeft ?? obj.slots_left, inventory.slotsLeft) ??
      inventory.slotsLeft
    if (inventory.capacity != null) {
      inventory.used = Math.max(0, inventory.capacity - inventory.slotsLeft)
    }
  }

  const cargo = airship?.cargo?.length ? airship.cargo : topCargo
  const cargoCount =
    pickNumber(
      obj.cargoCount ?? obj.cargo_count ?? airship?.cargoCount,
      cargo.length
    ) ?? cargo.length

  return {
    hangar,
    hasHangar:
      pickBool(obj.hasHangar ?? obj.has_hangar, hangar != null) || hangar != null,
    airship: hasAirshipFlag ? airship : null,
    purchaseCoins:
      pickNumber(
        obj.purchaseCoins ?? obj.purchase_coins ?? obj.buyPriceCoins,
        AIRSHIP_PURCHASE_COINS_DEFAULT
      ) ?? AIRSHIP_PURCHASE_COINS_DEFAULT,
    tripFeeCoins:
      pickNumber(
        obj.tripFeeCoins ?? obj.trip_fee_coins ?? obj.launchFeeCoins,
        AIRSHIP_TRIP_FEE_COINS_DEFAULT
      ) ?? AIRSHIP_TRIP_FEE_COINS_DEFAULT,
    canLaunch: pickBool(obj.canLaunch ?? obj.can_launch),
    activeMission: normalizeActiveMission(
      obj.activeMission ?? obj.active_mission ?? obj.mission
    ),
    cargo,
    cargoCount,
    inventory,
    raid: normalizeRaidConfig(obj.raid ?? obj.raidConfig ?? obj.raid_config),
    coins: pickNumber(obj.coins) ?? undefined,
    energy: pickNumber(obj.energy) ?? undefined,
  }
}

export function normalizeAirshipGeoBody(body: {
  pointId: string
  playerLat: number
  playerLng: number
}): AirshipGeoBody {
  const pointId = String(body.pointId ?? "").trim()
  const playerLat = Number(body.playerLat)
  const playerLng = Number(body.playerLng)
  if (!pointId || !Number.isFinite(playerLat) || !Number.isFinite(playerLng)) {
    throw new Error("Нужны корректные координаты GPS для ангара")
  }
  return {
    pointId,
    playerLat: Number(playerLat.toFixed(6)),
    playerLng: Number(playerLng.toFixed(6)),
  }
}

export function mapAirshipCities(data: unknown): AirshipCityOption[] {
  if (typeof data === "string" && data.trim()) {
    try {
      return mapAirshipCities(JSON.parse(data))
    } catch {
      return []
    }
  }
  const list = Array.isArray(data)
    ? data
    : (() => {
        const obj = unwrapPayload(data)
        const nested = obj.cities ?? obj.items ?? obj.destinations
        return Array.isArray(nested) ? nested : []
      })()

  return list
    .map((raw): AirshipCityOption | null => {
      if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
      const item = raw as RawRecord
      const cityId = pickNumber(item.cityId ?? item.city_id ?? item.id)
      if (cityId == null) return null
      return {
        cityId,
        title: pickString(item.title ?? item.name, `Город #${cityId}`),
        scannablePoints:
          pickNumber(
            item.scannablePoints ?? item.scannable_points ?? item.points,
            0
          ) ?? 0,
        totalPoints:
          pickNumber(item.totalPoints ?? item.total_points, 0) ?? 0,
        distanceKm:
          pickNumber(item.distanceKm ?? item.distance_km ?? item.distance, 0) ??
          0,
        energyRoundTrip:
          pickNumber(
            item.energyRoundTrip ?? item.energy_round_trip ?? item.energy,
            0
          ) ?? 0,
        flightLegMinutes:
          pickNumber(
            item.flightLegMinutes ??
              item.flight_leg_minutes ??
              item.legMinutes ??
              item.minutes,
            0
          ) ?? 0,
      }
    })
    .filter((city): city is AirshipCityOption => city != null)
}

function normalizeOwnerLeague(raw: unknown): DominantLeague | null {
  const leagueRaw = pickString(raw).toUpperCase()
  if (leagueRaw === "TITAN" || leagueRaw === "ATLANT" || leagueRaw === "NEUTRAL") {
    return leagueRaw
  }
  return null
}

export function normalizeAirshipMapMission(raw: unknown): AirshipMapMission | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  const id = item.id ?? item.missionId ?? item.mission_id
  const route = normalizeRoute(item.route)
  if (id == null || !route) return null

  const isOwn = pickBool(item.isOwn ?? item.is_own ?? item.mine)
  const ownerNickname = pickString(
    item.ownerNickname ?? item.owner_nickname ?? item.nickname
  )
  const nestedGuards = normalizeGuards(item.guards ?? item.guard)
  const guardTier = normalizeGuardTier(
    item.guardTier ??
      item.guard_tier ??
      item.defenseTier ??
      item.defense_tier ??
      nestedGuards.tier ??
      (typeof item.guard === "string" || typeof item.guard === "number"
        ? item.guard
        : null)
  )
  const guardPower =
    pickNumber(
      item.guardPower ??
        item.guard_power ??
        item.defensePower ??
        item.defense_power ??
        nestedGuards.power,
      0
    ) ?? 0
  const hasGuardSignal =
    guardTier != null ||
    guardPower > 0 ||
    nestedGuards.cardIds.length > 0 ||
    nestedGuards.cards.length > 0 ||
    pickBool(item.hasGuards ?? item.has_guards) ||
    (pickNumber(item.guardCount ?? item.guard_count, 0) ?? 0) > 0

  const hasIntel =
    isOwn ||
    pickBool(item.scouted ?? item.hasIntel ?? item.has_intel) ||
    Boolean(ownerNickname) ||
    hasGuardSignal ||
    item.cargoFullness != null ||
    item.cargo_fullness != null

  return {
    id: String(id),
    isOwn,
    route,
    currentPos: normalizeLatLng(
      item.currentPos ?? item.current_pos ?? item.position
    ),
    progress: Math.max(0, Math.min(1, pickNumber(item.progress, 0) ?? 0)),
    scouted: hasIntel,
    status: normalizeFlightStatus(item.status ?? item.phase),
    ownerNickname: ownerNickname || null,
    ownerLeague: normalizeOwnerLeague(
      item.ownerLeague ?? item.owner_league ?? item.league
    ),
    guardTier: guardTier ?? (hasGuardSignal ? "WEAK" : null),
    guardPower,
    cargoFullness: normalizeCargoFullness(
      item.cargoFullness ?? item.cargo_fullness
    ),
    departedAt: pickString(item.departedAt ?? item.departed_at) || null,
    arriveAt: pickString(item.arriveAt ?? item.arrive_at) || null,
    returnAt: pickString(item.returnAt ?? item.return_at) || null,
  }
}

export function mapAirshipMissions(data: unknown): AirshipMapMission[] {
  if (typeof data === "string" && data.trim()) {
    try {
      return mapAirshipMissions(JSON.parse(data))
    } catch {
      return []
    }
  }
  const list = Array.isArray(data)
    ? data
    : (() => {
        const obj = unwrapPayload(data)
        const nested = obj.missions ?? obj.items ?? obj.active
        return Array.isArray(nested) ? nested : []
      })()

  return list
    .map(normalizeAirshipMapMission)
    .filter((mission): mission is AirshipMapMission => mission != null)
}

export function normalizeAirshipClaimResponse(
  data: unknown
): AirshipClaimResponse | null {
  const obj = unwrapPayload(data)
  const status = normalizeAirshipStatus(obj.status ?? obj)
  if (!status) return null
  const cardsRaw = obj.cards ?? obj.claimedCards ?? obj.claimed_cards
  return {
    status,
    coins: pickNumber(obj.coins) ?? undefined,
    energy: pickNumber(obj.energy) ?? undefined,
    maxEnergy: pickNumber(obj.maxEnergy ?? obj.max_energy) ?? undefined,
    chakraCharges:
      pickNumber(obj.chakraCharges ?? obj.chakra_charges) ?? undefined,
    cards: Array.isArray(cardsRaw) ? normalizeGameCardsList(cardsRaw) : undefined,
    remainingCargoCount:
      pickNumber(
        obj.remainingCargoCount ?? obj.remaining_cargo_count ?? status.cargoCount
      ) ?? undefined,
    message: pickString(obj.message) || undefined,
  }
}

function normalizeCombatCard(raw: unknown): AirshipCombatCard | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  return {
    id: item.id != null ? String(item.id) : undefined,
    element: pickString(item.element) || undefined,
    colorRank: pickString(item.colorRank ?? item.color_rank) || undefined,
    level: pickNumber(item.level) ?? undefined,
    basePower: pickNumber(item.basePower ?? item.base_power) ?? undefined,
    style: pickString(item.style) || undefined,
  }
}

function normalizeCombatLog(raw: unknown): AirshipCombatLog | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  const attackCardsRaw = item.attackCards ?? item.attack_cards
  const defenseCardsRaw = item.defenseCards ?? item.defense_cards
  return {
    attackerNickname: pickString(
      item.attackerNickname ?? item.attacker_nickname,
      "Атакующий"
    ),
    defenderNickname: pickString(
      item.defenderNickname ?? item.defender_nickname,
      "Защитник"
    ),
    attackCards: Array.isArray(attackCardsRaw)
      ? attackCardsRaw
          .map(normalizeCombatCard)
          .filter((c): c is AirshipCombatCard => c != null)
      : [],
    defenseCards: Array.isArray(defenseCardsRaw)
      ? defenseCardsRaw
          .map(normalizeCombatCard)
          .filter((c): c is AirshipCombatCard => c != null)
      : [],
    attackRoll: pickNumber(item.attackRoll ?? item.attack_roll) ?? undefined,
    defensePower:
      pickNumber(item.defensePower ?? item.defense_power) ?? undefined,
    hullPower: pickNumber(item.hullPower ?? item.hull_power) ?? undefined,
    variance: pickNumber(item.variance) ?? undefined,
  }
}

function normalizeRaidOutcome(raw: unknown): AirshipRaidOutcome | null {
  const key = pickString(raw).toUpperCase()
  if (key === "WIN" || key === "LOSE") return key
  return null
}

export function normalizeAirshipRaidResult(data: unknown): AirshipRaidResult | null {
  const obj = unwrapPayload(data)
  const nestedRaid =
    obj.raid && typeof obj.raid === "object" && !Array.isArray(obj.raid)
      ? (obj.raid as RawRecord)
      : obj.attack && typeof obj.attack === "object" && !Array.isArray(obj.attack)
        ? (obj.attack as RawRecord)
        : null
  const source: RawRecord = nestedRaid ? { ...nestedRaid, ...obj } : obj

  const outcomeRaw = source.outcome
  // `result` may be nested payload (object) or outcome alias ("WIN"|"LOSE")
  const resultAlias =
    typeof source.result === "string" || typeof source.result === "number"
      ? source.result
      : null
  const outcome = normalizeRaidOutcome(outcomeRaw ?? resultAlias)
  if (!outcome) return null

  const combatLog =
    normalizeCombatLog(source.combatLog ?? source.combat_log) ??
    ({
      attackerNickname: pickString(
        source.attackerNickname ?? source.attacker_nickname,
        "Атакующий"
      ),
      defenderNickname: pickString(
        source.defenderNickname ?? source.defender_nickname,
        "Защитник"
      ),
      attackCards: [],
      defenseCards: [],
    } satisfies AirshipCombatLog)

  const stolenRaw = source.stolen ?? source.loot ?? source.cargo
  return {
    outcome,
    ptsAwarded: pickNumber(source.ptsAwarded ?? source.pts_awarded, 0) ?? 0,
    combatLog,
    stolen: Array.isArray(stolenRaw)
      ? stolenRaw
          .map(normalizeAirshipCargoItem)
          .filter((c): c is AirshipCargoItem => c != null)
      : [],
    consolationCoins:
      pickNumber(source.consolationCoins ?? source.consolation_coins) ??
      undefined,
    cargoFullness: normalizeCargoFullness(
      source.cargoFullness ?? source.cargo_fullness
    ),
    missionId:
      source.missionId != null || source.mission_id != null
        ? String(source.missionId ?? source.mission_id)
        : undefined,
    coins: pickNumber(source.coins) ?? undefined,
    energy: pickNumber(source.energy) ?? undefined,
    status: normalizeAirshipStatus(source.status) ?? undefined,
  }
}

export function mapAirshipRaids(data: unknown): AirshipRaidHistoryItem[] {
  if (typeof data === "string" && data.trim()) {
    try {
      return mapAirshipRaids(JSON.parse(data))
    } catch {
      return []
    }
  }
  const list = Array.isArray(data)
    ? data
    : (() => {
        const obj = unwrapPayload(data)
        const nested = obj.raids ?? obj.items ?? obj.history
        return Array.isArray(nested) ? nested : []
      })()

  return list
    .map((raw): AirshipRaidHistoryItem | null => {
      if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
      const item = raw as RawRecord
      const outcome = normalizeRaidOutcome(item.outcome ?? item.result)
      const combatLog = normalizeCombatLog(item.combatLog ?? item.combat_log)
      if (!outcome || !combatLog) return null
      const id = item.id ?? item.raidId ?? item.raid_id
      const stolenRaw = item.stolen ?? item.loot
      return {
        id: id != null ? String(id) : `${outcome}-${combatLog.attackerNickname}`,
        outcome,
        ptsAwarded: pickNumber(item.ptsAwarded ?? item.pts_awarded, 0) ?? 0,
        combatLog,
        stolen: Array.isArray(stolenRaw)
          ? stolenRaw
              .map(normalizeAirshipCargoItem)
              .filter((c): c is AirshipCargoItem => c != null)
          : [],
        consolationCoins:
          pickNumber(item.consolationCoins ?? item.consolation_coins) ??
          undefined,
        cargoFullness: normalizeCargoFullness(
          item.cargoFullness ?? item.cargo_fullness
        ),
        createdAt: pickString(item.createdAt ?? item.created_at) || null,
        attackerNickname:
          pickString(item.attackerNickname ?? item.attacker_nickname) ||
          combatLog.attackerNickname,
      }
    })
    .filter((raid): raid is AirshipRaidHistoryItem => raid != null)
}

export function groupAirshipCargo(items: AirshipCargoItem[]): AirshipCargoGroup[] {
  const groups = new Map<string, AirshipCargoGroup>()
  for (const item of items) {
    const key =
      item.missionId ??
      `${item.cityTitle ?? "unknown"}:${item.departedAt ?? ""}`
    const existing = groups.get(key)
    if (existing) {
      existing.items.push(item)
      continue
    }
    const dateLabel = item.departedAt
      ? formatAirshipDateShort(item.departedAt)
      : null
    const city = item.cityTitle ?? "Рейс"
    groups.set(key, {
      key,
      title: dateLabel ? `Рейс в ${city} · ${dateLabel}` : `Рейс в ${city}`,
      missionId: item.missionId,
      cityTitle: item.cityTitle,
      departedAt: item.departedAt ?? null,
      items: [item],
    })
  }
  return Array.from(groups.values())
}

export function formatAirshipDateShort(iso: string): string {
  const ms = Date.parse(iso)
  if (!Number.isFinite(ms)) return iso
  try {
    return new Date(ms).toLocaleDateString("ru-RU", {
      day: "numeric",
      month: "short",
    })
  } catch {
    return iso
  }
}

export function formatAirshipDuration(ms: number): string {
  const totalSec = Math.max(0, Math.ceil(ms / 1000))
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  if (h > 0) return `${h}ч ${String(m).padStart(2, "0")}м`
  if (m >= 10) return `${m}м ${String(s).padStart(2, "0")}с`
  return `${m}:${String(s).padStart(2, "0")}`
}

export function formatAirshipFlightLeg(minutes: number): string {
  const total = Math.max(0, Math.round(minutes))
  const roundTrip = total * 2
  if (total >= 60) {
    const h = Math.floor(total / 60)
    const m = total % 60
    return `~${h}ч ${m}м в одну сторону · рейс ~${Math.round(roundTrip / 60)}ч`
  }
  return `~${total} мин в одну сторону · рейс ~${roundTrip} мин`
}

export function formatGuardTier(tier: AirshipGuardTier | null | undefined): string {
  if (tier === "WEAK") return "Слабая"
  if (tier === "MEDIUM") return "Средняя"
  if (tier === "STRONG") return "Сильная"
  return "Нет охраны"
}

const GUARD_TIER_BY_RANK: Record<string, AirshipGuardTier> = {
  WHITE: "WEAK",
  GREEN: "WEAK",
  BLUE: "MEDIUM",
  PURPLE: "MEDIUM",
  ORANGE: "STRONG",
  RED: "STRONG",
}

const GUARD_TIER_RANK: Record<AirshipGuardTier, number> = {
  WEAK: 1,
  MEDIUM: 2,
  STRONG: 3,
}

/** Tier для UI: API + карты/сила; не «нет охраны», если охрана назначена. */
export function resolveDisplayGuardTier(
  guards: Pick<AirshipGuards, "tier" | "power" | "cardIds" | "cards"> | null | undefined
): AirshipGuardTier | null {
  if (!guards) return null
  const cardIds = Array.isArray(guards.cardIds) ? guards.cardIds : []
  const cards = Array.isArray(guards.cards) ? guards.cards : []
  const power = typeof guards.power === "number" ? guards.power : Number(guards.power) || 0
  const hasGuards = cardIds.length > 0 || cards.length > 0 || power > 0
  if (!hasGuards) return null

  let fromCards: AirshipGuardTier | null = null
  for (const card of cards) {
    const tier = GUARD_TIER_BY_RANK[String(card.colorRank ?? "").toUpperCase()]
    if (!tier) continue
    if (
      !fromCards ||
      GUARD_TIER_RANK[tier] > GUARD_TIER_RANK[fromCards]
    ) {
      fromCards = tier
    }
  }

  let fromPower: AirshipGuardTier | null = null
  if (power > 0) {
    if (power >= 2000) fromPower = "STRONG"
    else if (power >= 700) fromPower = "MEDIUM"
    else fromPower = "WEAK"
  }

  const api = guards.tier
  // Берём максимум из сигналов — чтобы BLUE + сила ~1300 не оставались WEAK/null
  const candidates = [api, fromCards, fromPower].filter(
    (t): t is AirshipGuardTier => t != null
  )
  if (candidates.length === 0) return "WEAK"
  return candidates.reduce((best, next) =>
    GUARD_TIER_RANK[next] > GUARD_TIER_RANK[best] ? next : best
  )
}

export function formatCargoFullness(
  fullness: AirshipCargoFullness | null | undefined
): string {
  if (fullness === "EMPTY") return "Пустой"
  if (fullness === "LOW") return "Мало"
  if (fullness === "HIGH") return "Полный"
  return "—"
}

export function cargoItemLabel(item: AirshipCargoItem): string {
  if (item.title) return item.title
  if (item.lootType === "CARD") {
    const rank = item.colorRank ?? item.card?.colorRank ?? "?"
    const level = item.card?.level
    const point = item.pointName ? ` · ${item.pointName}` : ""
    return `Карта · ${rank}${level != null ? ` Lv.${level}` : ""}${point}`
  }
  if (item.lootType === "COINS") return `Монеты · ${item.amount ?? "?"}`
  if (item.lootType === "ENERGY") return `Энергия · ${item.amount ?? "?"}`
  if (item.lootType === "CHARGES" || item.lootType === "CHAKRA") {
    return `Чакра · ${item.amount ?? "?"}`
  }
  return item.lootType
}

/** Клиентский lerp позиции по таймлайну миссии. */
export function lerpAirshipMissionPos(
  mission: Pick<
    AirshipMapMission,
    "status" | "route" | "departedAt" | "arriveAt" | "returnAt" | "currentPos"
  >,
  nowMs = Date.now()
): AirshipLatLng | null {
  if (mission.currentPos) return mission.currentPos
  const { from, to } = mission.route
  const departed = mission.departedAt ? Date.parse(mission.departedAt) : NaN
  const arrive = mission.arriveAt ? Date.parse(mission.arriveAt) : NaN
  const ret = mission.returnAt ? Date.parse(mission.returnAt) : NaN

  const mix = (a: AirshipLatLng, b: AirshipLatLng, t: number): AirshipLatLng => ({
    lat: a.lat + (b.lat - a.lat) * t,
    lng: a.lng + (b.lng - a.lng) * t,
  })

  if (
    mission.status === "IN_FLIGHT" &&
    Number.isFinite(departed) &&
    Number.isFinite(arrive) &&
    arrive > departed
  ) {
    const t = Math.max(0, Math.min(1, (nowMs - departed) / (arrive - departed)))
    return mix(from, to, t)
  }
  if (
    mission.status === "RETURNING" &&
    Number.isFinite(arrive) &&
    Number.isFinite(ret) &&
    ret > arrive
  ) {
    const t = Math.max(0, Math.min(1, (nowMs - arrive) / (ret - arrive)))
    return mix(to, from, t)
  }
  return mission.status === "RETURNING" ? from : to
}

export function resolveAirshipMissionEta(
  mission: Pick<AirshipActiveMission, "status" | "arriveAt" | "returnAt">
): { label: string; deadlineAt: string | null } {
  if (mission.status === "IN_FLIGHT") {
    return { label: "Прибытие", deadlineAt: mission.arriveAt }
  }
  return { label: "Возврат", deadlineAt: mission.returnAt }
}

/**
 * Доля прошедшего времени до ETA (0…1). Растёт по таймеру прибытия/возврата.
 * Без валидных дат — fallback на server progress.
 */
export function resolveAirshipMissionTimeProgress(
  mission: Pick<
    AirshipActiveMission,
    "status" | "departedAt" | "arriveAt" | "returnAt" | "progress"
  >,
  nowMs = Date.now()
): number {
  const departed = mission.departedAt ? Date.parse(mission.departedAt) : NaN
  const arrive = mission.arriveAt ? Date.parse(mission.arriveAt) : NaN
  const ret = mission.returnAt ? Date.parse(mission.returnAt) : NaN

  if (
    mission.status === "IN_FLIGHT" &&
    Number.isFinite(departed) &&
    Number.isFinite(arrive) &&
    arrive > departed
  ) {
    return Math.max(0, Math.min(1, (nowMs - departed) / (arrive - departed)))
  }
  if (
    mission.status === "RETURNING" &&
    Number.isFinite(arrive) &&
    Number.isFinite(ret) &&
    ret > arrive
  ) {
    return Math.max(0, Math.min(1, (nowMs - arrive) / (ret - arrive)))
  }
  return Math.max(0, Math.min(1, mission.progress ?? 0))
}

export function resolveAirshipCargo(status: AirshipStatus | null): AirshipCargoItem[] {
  if (!status) return []
  if (status.airship?.cargo?.length) return status.airship.cargo
  return status.cargo
}

export function resolveAirshipCargoCount(status: AirshipStatus | null): number {
  if (!status) return 0
  if (status.airship) {
    return status.airship.cargoCount || status.airship.cargo.length
  }
  return status.cargoCount || status.cargo.length
}

export function airshipUiState(
  status: AirshipStatus | null
):
  | "no_hangar"
  | "no_airship"
  | "docked_ready"
  | "docked_full"
  | "in_mission"
  | "has_cargo" {
  if (!status?.hasHangar) return "no_hangar"
  if (!status.airship) return "no_airship"
  const cargoCount = resolveAirshipCargoCount(status)
  if (cargoCount > 0) return "has_cargo"
  if (status.airship.status === "IN_MISSION" || status.activeMission) {
    return "in_mission"
  }
  if (status.inventory.slotsLeft < 1) return "docked_full"
  return "docked_ready"
}
